import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col } from 'antd';
import { lookupInventoryByVins } from '../../actions/Actions';
import { DEFAULT_VEHICLE_IMAGE } from './constants';

function extractModel(vehicle) {
  // vehicle like "2024 Ford Mustang" => model key "Ford Mustang"
  if (!vehicle) return '';
  const parts = vehicle.split(' ');
  if (parts.length < 3) return vehicle;
  return parts.slice(1).join(' ');
}

function computeTopCars(rightSwipesData, topN) {
  // Group by normalized model label; keep first seen VIN for that model
  const byModel = new Map();
  rightSwipesData.forEach(item => {
    const modelKeyRaw = extractModel(item.vehicle);
    const modelKey = modelKeyRaw.replace(/\s+LT|\s+XLT|\s+SE|\s+LE|\s+XLE|\s+EX-L|\s+Sport|\s+ST/gi, '').trim();
    const vin = (item && item.vin) ? String(item.vin).toUpperCase() : '';
    const entry = byModel.get(modelKey) || { model: modelKey, count: 0, vin: vin, vehicle: item.vehicle };
    entry.count += 1;
    // Prefer first non-empty VIN as representative for this model
    if (!entry.vin && vin) entry.vin = vin;
    if (!entry.vehicle && item.vehicle) entry.vehicle = item.vehicle;
    byModel.set(modelKey, entry);
  });
  return Array.from(byModel.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

const HotCarsComponent = ({ rightSwipesData, topN = 5, embedded = false, compact = false, compactHeight = 160 }) => {
  const topCars = useMemo(() => computeTopCars(rightSwipesData, topN), [rightSwipesData, topN]);
  const [enriched, setEnriched] = useState(topCars);

  useEffect(() => {
    let cancelled = false;
    async function enrich() {
      const vins = Array.from(new Set((topCars || []).map(t => t.vin).filter(Boolean)));
      if (vins.length === 0) {
        setEnriched(topCars);
        return;
      }
      try {
        const items = await lookupInventoryByVins(vins);
        const byVin = new Map((items || []).map(i => [String(i.vin).toUpperCase(), i]));
          const merged = (topCars || []).map(t => {
            const inv = byVin.get(String(t.vin || '').toUpperCase());
            if (!inv) {
            return { ...t, image: DEFAULT_VEHICLE_IMAGE, price: null };
            }
            return {
              ...t,
            image: inv.photo || inv.photo_url || DEFAULT_VEHICLE_IMAGE,
            price: inv.price != null ? Number(inv.price) : null,
            stock: inv.stock_no || ''
            };
          });
        if (!cancelled) setEnriched(merged);
      } catch (e) {
        if (!cancelled) setEnriched(topCars);
      }
    }
    enrich();
    return () => { cancelled = true; };
  }, [topCars]);

  const content = (
    <>
      
      <Row gutter={[compact ? 8 : 12, compact ? 8 : 12]} style={{ marginTop: compact ? 8 : 12 }}>
        {enriched.map((car, idx) => {
          const imageUrl = car.image || DEFAULT_VEHICLE_IMAGE;
          const mediaHeight = compact ? compactHeight : 180;
          return (
            <Col xs={24} sm={24} md={24} key={car.model}>
              <div className="hotcar-card">
                <div
                  className="hotcar-media"
                  style={{
                    height: mediaHeight,
                    backgroundImage: `url(${DEFAULT_VEHICLE_IMAGE})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={car.model}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_VEHICLE_IMAGE;
                    }}
                  />
                  <div className={`hotcar-rank rank-${idx + 1}`}>#{idx + 1}</div>
                  <div className="hotcar-overlay" />
                  <div className="hotcar-titlebar">
                    <div className="hotcar-title">{car.model}</div>
                    <div className="hotcar-sub">
                      {car.price != null ? `$${car.price.toLocaleString()}` : ''}
                      {car.price != null ? ' • ' : ''}{car.count} swipes
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );

  if (embedded) {
    return (
      <div>
        {content}
      </div>
    );
  }

  return (
    <Card>
      {content}
    </Card>
  );
};

export default HotCarsComponent;


